import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { drugCatalogQueries } from '../../queries/drug-catalog.queries.js';
import { zql } from '../../schema.js';
import { itemsDetail, statusItems, valueItem } from '../../util/event-log-items.js';
import { findDrugName, nameOf } from '../utils/log-names.js';
import { CompoundMappingType, CompoundCarrierType } from '../../database/schema.js';
import { pickActorFromUserActions, pickTimestampFromUserActions, titleCase } from '../../util/index.js';
import { Status } from '../../util/schemas.js';
import { assertCan } from '../../utils.js';
import { DeleteResourceSchema, EventLogResources, UpdateResourceStatusSchema, addEventLog, } from '../util.js';
import { AddDrugItemSchema, createDrugCatalogItem, createDrugItemCategories, createMedicationProfiles, replaceDrugItemCategories, replaceMedicationProfiles, updateDrugCatalogItem, } from './utils/index.js';
import { upsertCompoundMapping } from '../compounders/utils.js';
export const SolutionSchema = AddDrugItemSchema.omit({ catalogType: true }).extend({
    concentrationValue: z.number().min(0),
    concentrationUnitId: z.uuidv7().nonempty(),
    types: z.array(z.uuidv7()).nonempty(),
    /**
     * Smart compounder carrier type (0-3, see `carrierType` on compound_mappings).
     * Only meaningful for carrier solutions; stored on the CARRIER mapping.
     */
    carrierType: CompoundCarrierType.optional(),
});
export const AddSolutionSchema = SolutionSchema;
export const UpdateSolutionSchema = SolutionSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
async function findById(tx, ctx, args) {
    return await tx.run(drugCatalogQueries.getSolutionById.fn({ args, ctx }));
}
async function createSolutionDetails(tx, args, userActions) {
    const newSolution = {
        concentrationValue: args.concentrationValue,
        concentrationUnitId: args.concentrationUnitId,
        drugCatalogItemId: args.drugItemId,
        ...userActions,
    };
    const promises = [tx.mutate.solutionDetails.insert(newSolution)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newSolution);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.solutionDetails,
            resourceId: newSolution.drugCatalogItemId,
            action: 'create',
            patches: patches,
            performedBy: pickActorFromUserActions(userActions),
            performedAt: pickTimestampFromUserActions(userActions),
            status: 'OK',
            details: { id: newSolution.drugCatalogItemId, name: titleCase(args.name) },
        }));
    }
    await Promise.all(promises);
    return newSolution;
}
async function updateSolutionDetails(tx, prev, args, userActions) {
    const updatedSolution = {
        concentrationValue: args.concentrationValue,
        concentrationUnitId: args.concentrationUnitId,
        drugCatalogItemId: args.id,
        ...userActions,
    };
    const promises = [tx.mutate.solutionDetails.update(updatedSolution)];
    if (tx.location === 'server') {
        const patches = createPatch(prev.solutionDetails, updatedSolution);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.solutionDetails,
            resourceId: updatedSolution.drugCatalogItemId,
            action: 'update',
            patches: patches,
            performedBy: pickActorFromUserActions(userActions),
            performedAt: pickTimestampFromUserActions(userActions),
            status: 'OK',
            details: { id: args.id, name: prev.name },
        }));
    }
    await Promise.all(promises);
    return updatedSolution;
}
/** Drug name and solution type names for solution type logs. */
async function solutionTypeLogNames(tx, drugId, typeIds) {
    const types = typeIds.length ? await tx.run(zql.solutionTypes.where('id', 'IN', typeIds)) : [];
    return { itemName: await findDrugName(tx, drugId), type: nameOf(types) };
}
async function createSolutionDetailTypes(tx, args, userActions) {
    const names = tx.location === 'server' ? await solutionTypeLogNames(tx, args.solutionDetailId, args.types) : null;
    const relations = [...new Set(args.types)].flatMap(typeId => {
        const newSolutionDetailType = {
            id: uuidv7(),
            solutionTypeId: typeId,
            solutionDetailId: args.solutionDetailId,
            ...userActions,
        };
        const promises = [tx.mutate.solutionDetailTypes.insert(newSolutionDetailType)];
        if (names) {
            const patches = createPatch({}, newSolutionDetailType);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.solutionDetailType,
                resourceId: newSolutionDetailType.id,
                action: 'create',
                patches: patches,
                performedBy: pickActorFromUserActions(userActions),
                performedAt: pickTimestampFromUserActions(userActions),
                status: 'OK',
                details: { itemName: names.itemName, ...itemsDetail(valueItem('solution_type', names.type(typeId))) },
            }));
        }
        return promises;
    });
    await Promise.all(relations);
}
export async function replaceSolutionDetailTypes(tx, { drugItem, newSolutionTypesIds }, userActions) {
    const currentSolutionTypes = new Set(drugItem.solutionDetails?.types?.map(item => item.solutionTypeId));
    const newTypesIds = new Set(newSolutionTypesIds);
    const addedTypes = newSolutionTypesIds.filter(id => !currentSolutionTypes.has(id));
    const deletedTypes = drugItem.solutionDetails?.types?.filter(item => !newTypesIds.has(item.solutionTypeId)) || [];
    const userActionsTimestamp = pickTimestampFromUserActions(userActions);
    const userActionsActor = pickActorFromUserActions(userActions);
    if (addedTypes.length) {
        await createSolutionDetailTypes(tx, { types: addedTypes, solutionDetailId: drugItem.id }, { createdAt: userActionsTimestamp, createdBy: userActionsActor });
    }
    if (deletedTypes.length) {
        const names = tx.location === 'server'
            ? await solutionTypeLogNames(tx, drugItem.id, deletedTypes.map(c => c.solutionTypeId))
            : null;
        const relationsToMarkAsDeleted = deletedTypes.flatMap(c => {
            const updatedRelation = {
                id: c.id,
                deletedAt: userActionsTimestamp,
                deletedBy: userActionsActor,
            };
            const promises = [tx.mutate.solutionDetailTypes.update(updatedRelation)];
            if (names) {
                const patches = createPatch(c, updatedRelation);
                promises.push(addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.solutionDetailType,
                    resourceId: updatedRelation.id,
                    action: 'delete',
                    patches: patches,
                    performedBy: userActionsActor,
                    performedAt: userActionsTimestamp,
                    status: 'WARNING',
                    details: {
                        itemName: names.itemName,
                        ...itemsDetail(valueItem('solution_type', names.type(c.solutionTypeId))),
                    },
                }));
            }
            return promises;
        });
        await Promise.all(relationsToMarkAsDeleted);
    }
}
export const solutions = {
    add: defineMutator(AddSolutionSchema, async ({ ctx, tx, args }) => {
        assertCan(ctx, 'catalogue.manage');
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const newDrugItem = await createDrugCatalogItem(tx, { ...args, catalogType: 'SOLUTION' }, userActions);
        const newSolutionDetail = await createSolutionDetails(tx, { ...args, drugItemId: newDrugItem.id }, userActions);
        await createSolutionDetailTypes(tx, { ...args, solutionDetailId: newSolutionDetail.drugCatalogItemId }, userActions);
        await createDrugItemCategories(tx, { categories: args.categoriesIds, drugItemId: newDrugItem.id }, userActions);
        await createMedicationProfiles(tx, { medicationProfiles: args.medicationProfiles, drugItemId: newDrugItem.id }, userActions);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.CARRIER,
            sourceId: newDrugItem.id,
            machineId: args.machineId,
            carrierType: args.carrierType,
            actorId: ctx.user.id,
            now,
        });
    }),
    update: defineMutator(UpdateSolutionSchema, async ({ ctx, tx, args }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await updateDrugCatalogItem(tx, prev, args, userActions);
        await updateSolutionDetails(tx, prev, args, userActions);
        await replaceSolutionDetailTypes(tx, { drugItem: prev, newSolutionTypesIds: args.types || [] }, userActions);
        await replaceDrugItemCategories(tx, { drugItem: prev, newDrugCategoriesIds: args.categoriesIds || [] }, userActions);
        await replaceMedicationProfiles(tx, { drugItem: prev, newMedicationProfiles: args.medicationProfiles || [] }, userActions);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.CARRIER,
            sourceId: args.id,
            machineId: args.machineId,
            carrierType: args.carrierType,
            actorId: ctx.user.id,
            now,
        });
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedDrugItem = {
            id: args.id,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.drugCatalogItems.update(updatedDrugItem);
        if (tx.location === 'server') {
            const patches = createPatch({ status: prev.status }, { status: args.status });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugCatalogItem,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedDrugItem.updatedBy,
                performedAt: updatedDrugItem.updatedAt,
                status: args.status === Status.enum.inactive ? 'WARNING' : 'INFO',
                details: { id: args.id, name: prev.name, ...itemsDetail(statusItems(prev.status, args.status)) },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedDrug = {
            id: args.id,
            code: `${prev.code}_deleted_${now}`,
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        await tx.mutate.drugCatalogItems.update(updatedDrug);
        if (tx.location === 'server') {
            const patches = createPatch({ code: prev.code }, { code: updatedDrug.code });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugCatalogItem,
                resourceId: args.id,
                action: 'delete',
                patches: patches,
                performedBy: updatedDrug.deletedBy,
                performedAt: updatedDrug.deletedAt,
                status: 'WARNING',
                details: { id: args.id, name: prev.name },
            }, ctx);
        }
        // await Promise.all(
        //   prev.categories.flatMap(c => {
        //     const updatedRelation = {
        //       id: c.id,
        //       deletedAt: updatedSubstance.deletedAt,
        //       deletedBy: updatedSubstance.deletedBy,
        //     }
        //     const promises = [tx.mutate.activeSubstanceCategories.update(updatedRelation)]
        //     if (tx.location === 'server') {
        //       const patches = createPatch({}, updatedRelation)
        //       promises.push(
        //         addEventLog(tx, {
        //           id: uuidv7(),
        //           resource: EventLogResources.activeSubstanceCategoryRelation,
        //           resourceId: updatedRelation.id,
        //           action: 'delete',
        //           patches: patches,
        //           performedBy: updatedRelation.deletedBy,
        //           performedAt: updatedRelation.deletedAt,
        //         })
        //       )
        //     }
        //     return promises
        //   })
        // )
    }),
};
//# sourceMappingURL=solution.mutators.js.map